import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { compose, withProps } from 'recompose';
import SelectInput from '@/se/components/inputs/SelectInput';
import TextInput from '@/se/components/inputs/TextInput';
import { withLabel } from '@/se/components/Label';
import { DelayMilestone, OTHER_REASON_VALUE, delayReasonOptions, parseDelayReasonValue } from './delayReasons';

/**
 * Shared se-components input for picking a PACU milestone delay reason.
 *
 * Conforms to the { value, onChange } input contract so it drops directly into
 * `withProps({ schema: {...} })(ObjectInput)` forms. It reports a single
 * `string | null` upward: the selected option slug, the custom free text when
 * "other" is chosen, or `null` when cleared.
 */

export type DelayReasonInputProps = {
  delayType: DelayMilestone;
  value?: string | null;
  onChange: (value: string | null) => void;
  hasError?: boolean;
  error?: string;
  className?: string;
  isDisabled?: boolean;
  name?: string;
};

const Wrapper = styled.div`
  display: flex;
  flex-flow: column;
  gap: 0.75rem;
`;

const DelayReasonInput = ({
  delayType,
  value,
  onChange,
  hasError,
  error,
  className,
  isDisabled,
  name,
}: DelayReasonInputProps) => {
  // Local UI state so the transient "other selected, no text yet" state (which
  // cannot be represented by the single reported string) survives re-renders.
  const [selected, setSelected] = useState<string | null>(null);
  const [custom, setCustom] = useState('');
  // Tracks the last value we reported upward, so external value changes (e.g.
  // form reset / prefill) re-sync local state without clobbering user edits.
  const lastReported = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (value === lastReported.current) {
      return;
    }
    const parsed = parseDelayReasonValue(delayType, value ?? null);
    if (parsed.kind === 'none') {
      setSelected(null);
      setCustom('');
    } else if (parsed.kind === 'other') {
      setSelected(OTHER_REASON_VALUE);
      setCustom(parsed.custom);
    } else {
      setSelected(parsed.option.value);
      setCustom('');
    }
    lastReported.current = value ?? null;
  }, [value, delayType]);

  const report = useCallback(
    (next: string | null) => {
      lastReported.current = next;
      onChange(next);
    },
    [onChange]
  );

  const handleSelect = useCallback(
    (slug?: string | null) => {
      if (!slug) {
        setSelected(null);
        setCustom('');
        report(null);
      } else if (slug === OTHER_REASON_VALUE) {
        setSelected(OTHER_REASON_VALUE);
        report(custom ? custom : null);
      } else {
        setSelected(slug);
        setCustom('');
        report(slug);
      }
    },
    [custom, report]
  );

  const handleCustom = useCallback(
    (text: string) => {
      setCustom(text);
      report(text ? text : null);
    },
    [report]
  );

  return (
    <Wrapper className={className}>
      <SelectInput
        name={name}
        options={delayReasonOptions[delayType]}
        value={selected}
        onChange={handleSelect}
        isClearable={true}
        isSearchable={false}
        disabled={isDisabled}
        hasError={hasError}
        error={error}
        placeholder="Select a delay reason"
      />
      {selected === OTHER_REASON_VALUE && (
        <TextInput value={custom} onChange={handleCustom} isDisabled={isDisabled} placeholder="Enter a custom reason" />
      )}
    </Wrapper>
  );
};

export default DelayReasonInput;

/**
 * Composed schema entry for use inside ObjectInput form schemas, e.g.
 * `{ reason: DelayReasonSchemaEntry('oob') }`. Matches the repo's
 * compose(withLabel(...), withProps(...)) idiom (see PatientEventEdit).
 */
export const DelayReasonSchemaEntry = (delayType: DelayMilestone) =>
  compose(withLabel('Delay Reason'), withProps({ delayType }))(DelayReasonInput as React.ComponentType<any>);
